<template>
    <div>
        <q-table
            :title="$t('Product Properties')"
            :rows="properties"
            :columns="tableColumns"
            :grid="$q.screen.lt.md"
            :filter="search"
            v-model:pagination="pagination"
            :rows-per-page-options="[pagination.rowsPerPage]"
            :no-data-label="
                $t(
                    'No product properties created yet. Click on Create Product Property button to add a new one.'
                )
            "
            row-key="name"
            table-style="max-height: 100%;"
            binary-state-sort
            flat
        >
            <!-- top bar -->
            <template v-slot:top>
                <!-- table title -->
                <div class="col-12">
                    <div class="text-h4 text-weight-medium">
                        {{ $t('Product Properties') }}
                    </div>
                    <div class="page-description">
                        {{ $t('Create and modify products properties.') }}
                    </div>
                </div>
                <div
                    class="row fit"
                    :class="
                        $q.screen.lt.sm
                            ? ''
                            : 'wrap justify-between items-start content-start'
                    "
                >
                    <!-- search bar -->
                    <q-input
                        v-model="search"
                        @clear="search = ''"
                        :placeholder="$t('Search product properties')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        debounce="750"
                        clearable
                        outlined
                        dense
                        for="search-user-input"
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="fas fa-search" />
                        </template>
                    </q-input>

                    <!-- Create product category button -->
                    <q-btn
                        v-show="isUserAuthorized"
                        @click="
                            $router.push({
                                name: 'product_property_detail'
                            })
                        "
                        :label="$t('Create Product Property')"
                        class="table-header__button"
                        :class="$q.screen.lt.sm ? 'col-12' : ''"
                        color="accent"
                        size="md"
                    ></q-btn>
                </div>
            </template>
            <!-- table body -->
            <template v-slot:body="props">
                <q-tr :props="props">
                    <q-td key="name" :props="props">
                        {{ props.row.name }}
                    </q-td>
                    <q-td key="product_category" :props="props">
                        {{ getProductCategoryName(props.row.product_category) }}
                    </q-td>
                    <q-td key="actions" :props="props">
                        <div class="row q-gutter-md">
                            <!-- "edit product property" button -->
                            <q-btn
                                :disable="!isUserAuthorized"
                                @click="edit(props.row)"
                                class="table__action-button edit-user-btn"
                                color="accent"
                                icon="fas fa-edit"
                                outline
                            >
                                <q-tooltip v-if="!isUserAuthorized">
                                    {{
                                        $t(
                                            'You are not authorized to edit this product property'
                                        )
                                    }}
                                </q-tooltip>
                            </q-btn>
                            <!-- "remove product property" button -->
                            <q-btn
                                :disable="!isUserAuthorized"
                                @click="remove(props.row)"
                                class="table__action-button"
                                color="red"
                                icon="fas fa-trash-alt"
                                outline
                            >
                                <q-tooltip v-if="!isUserAuthorized">
                                    {{
                                        $t(
                                            'You are not authorized to delete this product property'
                                        )
                                    }}
                                </q-tooltip>
                            </q-btn>
                        </div>
                    </q-td>
                </q-tr>
            </template>
            <template v-slot:item="props">
                <div
                    class="q-pa-md col-xs-12 col-sm-6 col-md-4 col-lg-3 grid-style-transition"
                >
                    <q-card>
                        <q-list dense>
                            <q-item>
                                <q-item-section>
                                    <q-item-label class="q-mt-sm">
                                        <span class="q-mr-sm">
                                            <strong> {{ $t('Name') }}: </strong>
                                        </span>
                                        <span>
                                            {{ props.row.name }}
                                        </span>
                                    </q-item-label>
                                    <q-item-label class="q-mt-sm">
                                        <span class="q-mr-sm">
                                            <strong>
                                                {{ $t('Product Category') }}:
                                            </strong>
                                        </span>
                                        <span>
                                            {{
                                                getProductCategoryName(
                                                    props.row.product_category
                                                )
                                            }}
                                        </span>
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                        </q-list>
                        <q-card-section>
                            <q-btn
                                :disable="!isUserAuthorized"
                                @click="edit(props.row)"
                                class="q-mr-sm"
                                color="accent"
                                icon="fas fa-edit"
                                outline
                            />
                            <!-- "remove product property" button -->
                            <q-btn
                                :disable="!isUserAuthorized"
                                @click="remove(props.row)"
                                class="table__action-button"
                                color="red"
                                icon="fas fa-trash-alt"
                                outline
                            >
                                <q-tooltip>
                                    {{
                                        $t(
                                            'You are not authorized to delete this product property'
                                        )
                                    }}
                                </q-tooltip>
                            </q-btn>
                        </q-card-section>
                    </q-card>
                </div>
            </template>
        </q-table>
    </div>
</template>
<script>
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useProductsStore } from '@stores/products.js';

export default {
    name: 'ProductProperties',
    created() {
        this.pagination.rowsNumber = this.properties.length;
    },
    computed: {
        ...mapState(useProductsStore, [
            'getProductProperties',
            'getProductCategories'
        ]),
        /**
         * Filter product properties based on search query
         * @returns {Array} filtered product properties
         */
        properties() {
            return this.getProductProperties.filter(property =>
                property.name.toLowerCase().includes(this.search.toLowerCase())
            );
        },
        /**
         * Check if user is authorized to create, edit or delete a product property
         * @returns {Boolean} is user authorized
         */
        isUserAuthorized() {
            return this.$isUserAdmin() || this.$isUserManager();
        }
    },
    data() {
        return {
            storedProperties: this.getProductProperties,
            pagination: {
                sortBy: '',
                descending: false,
                page: 1,
                rowsPerPage: 30,
                rowsNumber: null
            },
            search: '',
            tableColumns: [
                {
                    name: 'name',
                    align: 'left',
                    label: this.$t('Name'),
                    field: 'name',
                    sortable: true
                },
                {
                    name: 'product_category',
                    label: this.$t('Product Category'),
                    field: 'product_category',
                    align: 'left',
                    sortable: true
                },
                {
                    name: 'actions',
                    label: this.$t('Actions'),
                    field: 'actions',
                    align: 'left'
                }
            ]
        };
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useProductsStore, ['removeProductPropertyAction']),
        /**
         * Edit a product category
         * @param {Object} row
         */
        edit(row) {
            this.$router.push({
                name: 'product_property_detail',
                state: {
                    id: row.id,
                    name: row.name,
                    productCategory: row.product_category,
                    allowedValues: JSON.stringify(row.allowed_values)
                }
            });
        },
        /**
         * Remove a product category
         * @param {Object} row
         */
        async remove(row) {
            try {
                // begin loading
                this.beginLoading(`${this.$t('Removing product property')}...`);
                await this.removeProductPropertyAction(row);
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to remove product property. Please try again later.'
                    ),
                    error
                );
            } finally {
                // end loading
                this.stopLoading();
            }
        },
        /**
         * Get product category name by id
         * @param {Number} id
         * @returns {String} category name
         */
        getProductCategoryName(id) {
            if (id) {
                const category = this.getProductCategories.find(
                    category => category.id === id
                );
                return category ? category.name : '';
            }
            return '';
        }
    }
};
</script>
